/**
 * "Why this case needs attention": the entity rules on a case, in plain words.
 *
 * The server evaluates the rules of the case's company and sends one reason per rule and
 * line (what is wrong) with an action (what sales does next). This panel lists them:
 * reasons that hold the case first (in a danger alert), then the ones to check, and the
 * notes in a collapsed "Other notes". Each reason carries its rule id and company in
 * small text and a "Why?" link that explains the rule (features/rules); managers and
 * admins also get a link to the live settings. A reason on one line is also shown on that
 * line (`LineRuleFlags`).
 *
 * A price below the contribution threshold (`pricing.margin`) holds the case until a
 * manager approves it or a person changes the price. That reason carries the next step:
 * where a viewer who may approve prices presses "Approve price" (on the line, in the bar
 * or under "More"), and who acts for everyone else. For a viewer who may approve, the
 * reason and the line show a working "Approve price" (ease round, 13 Oct 2026: the
 * approval was six clicks deep, and the button here was a disabled pointer to it). Other
 * viewers see no such button.
 *
 * The panel itself changes no rule; "Approve price" is the one write, passed in by the page.
 */

import { Check, Icon } from "@norican/icons";
import { Alert, Badge, Button, Card, Collapsible, Typography } from "@norican/react";
import { useState } from "react";
import { Link } from "react-router";

import { WhyLink } from "@/features/rules";
import { LearnThisLink } from "@/features/training/learn-this";
import { type CaseRules, type RuleReason, useMe } from "@/lib/api";
import { hasCapability, useAreaLabels } from "@/lib/areas";
import { logger } from "@/lib/logger";
import {
  CUSTOMER_BASIS_TEXT,
  entityLabel,
  groupReasons,
  type ReasonGroup,
  reasonsOf,
  RULES_CHANGED_SENTENCE,
  SEVERITY_META,
  withSignalWords,
} from "@/lib/rules";

/** The panel title (quoted by the help). */
export const RULES_PANEL_TITLE = "Why this case needs attention";
/** The title of the alert over the reasons that hold the case. */
export const RULES_HELD_TITLE = "Held until you clear these";
/** The collapsed section with the info notes. */
export const RULES_NOTES_LABEL = "Other notes";
/** The rule whose reason is cleared by a manager's price approval. */
export const PRICE_APPROVAL_RULE = "pricing.margin";

/**
 * The next step on a price that needs an approval. `area` is the area name of the case
 * or null. The second argument ("Line 2" or null) is kept for the callers: the sentence
 * is the same for every line, the button sits on the line the reason names.
 */
export function priceApprovalHint(
  mayApprove: boolean,
  _line: string | null | undefined,
  area: string | null | undefined,
): string {
  if (mayApprove) {
    return "To approve: press Approve price on the line, in the bar or under More.";
  }
  const who = area ? `A manager of ${area}` : "A manager of this area";
  return `${who} approves this price on the case page. You can also change the price.`;
}

/** The reason and how to clear it, for the hint under a held price. */
interface HintContext {
  mayApprove: boolean;
  area: string | null;
  /** Approves the held price of a line by its key; absent for a viewer who may not. */
  onApprovePrice?: (lineKey: string) => void;
}

/** True when the message already names the line ("line 2", any case). */
function mentionsLabel(message: string, label: string): boolean {
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`\\b${escaped}\\b`, "i").test(message);
}

function ReasonItem({
  reason,
  lineLabel,
  entity,
  hint,
}: {
  reason: RuleReason;
  lineLabel?: (lineKey: string) => string | null;
  entity?: string | null;
  hint?: HintContext;
}) {
  const meta = SEVERITY_META[reason.severity];
  const label = reason.scope === "line" && reason.line_key ? lineLabel?.(reason.line_key) : null;
  // The server often names the line itself ("Line 2 has a lead time of 95 days."): then no
  // "Line 2:" in front, it would say it twice.
  const line = label && !mentionsLabel(reason.message, label) ? label : null;
  const nextStep =
    hint && reason.rule_id === PRICE_APPROVAL_RULE
      ? priceApprovalHint(hint.mayApprove, label, hint.area)
      : null;
  return (
    <li
      className="flex min-w-0 items-start gap-2"
      data-rule={reason.rule_id}
      data-severity={reason.severity}
    >
      <Badge tone={meta.tone} size="xs" className="mt-0.5 shrink-0">
        {meta.label}
      </Badge>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-fg text-sm break-words">
          {line && <span className="font-semibold">{line}: </span>}
          {withSignalWords(reason.message)}
        </span>
        {reason.action && (
          <span className="text-fg-muted text-xs break-words">{reason.action}</span>
        )}
        {nextStep && (
          <span className="text-fg text-xs font-semibold break-words" data-next-step>
            {nextStep}
          </span>
        )}
        {nextStep &&
          hint?.mayApprove &&
          hint.onApprovePrice &&
          reason.severity === "blocking" &&
          reason.line_key && (
            <Button
              size="sm"
              variant="outline"
              tone="accent"
              className="w-fit"
              startIcon={<Icon icon={Check} size="xs" />}
              aria-label={label ? `Approve the price of ${label}` : "Approve price"}
              onClick={() => hint.onApprovePrice?.(reason.line_key ?? "")}
              data-approve-price={reason.line_key}
            >
              Approve price
            </Button>
          )}
        {nextStep && <LearnThisLink spot="price-approval" />}
        <span className="flex flex-wrap items-center gap-x-2">
          <WhyLink
            ruleId={reason.rule_id}
            entity={reason.entity || entity}
            severity={reason.severity}
            className="h-auto px-1 py-0.5 text-xs"
          />
        </span>
      </span>
    </li>
  );
}

/** The shown number of a line ("Line 5" → 5) through the page's line label. */
function lineNumberOf(lineLabel?: (lineKey: string) => string | null) {
  return (lineKey: string): number | null => {
    const match = /(\d+)/.exec(lineLabel?.(lineKey) ?? "");
    return match ? Number(match[1]) : null;
  };
}

/** Several lines with the same reason: one entry, every line on request. */
function GroupItem({
  group,
  lineLabel,
  entity,
  hint,
}: {
  group: ReasonGroup;
  lineLabel?: (lineKey: string) => string | null;
  entity?: string | null;
  hint?: HintContext;
}) {
  const [open, setOpen] = useState(false);
  const first = group.reasons[0];
  if (!first) {
    return null;
  }
  const meta = SEVERITY_META[first.severity];
  return (
    <li
      className="flex min-w-0 items-start gap-2"
      data-rule={first.rule_id}
      data-severity={first.severity}
      data-grouped={group.reasons.length}
    >
      <Badge tone={meta.tone} size="xs" className="mt-0.5 shrink-0">
        {meta.label}
      </Badge>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-fg text-sm break-words">
          <span className="font-semibold">{group.linesText}: </span>
          {withSignalWords(first.message)}
          {group.reasons.length > 1 && ` (and ${group.reasons.length - 1} more like it)`}
        </span>
        {first.action && <span className="text-fg-muted text-xs break-words">{first.action}</span>}
        <Collapsible.Root open={open} onOpenChange={setOpen}>
          <Collapsible.Trigger className="text-accent-fg-subtle w-fit text-xs font-semibold hover:underline">
            {open ? "Hide the lines" : `Show all ${group.reasons.length}`}
          </Collapsible.Trigger>
          <Collapsible.Panel>
            <ul className="mt-1 flex flex-col gap-1">
              {group.reasons.map((reason, index) => (
                <ReasonItem
                  key={`${reason.line_key ?? ""}-${index}`}
                  reason={reason}
                  lineLabel={lineLabel}
                  entity={entity}
                  hint={hint}
                />
              ))}
            </ul>
          </Collapsible.Panel>
        </Collapsible.Root>
      </span>
    </li>
  );
}

function ReasonList({
  reasons,
  lineLabel,
  label,
  entity,
  hint,
}: {
  reasons: RuleReason[];
  lineLabel?: (lineKey: string) => string | null;
  label: string;
  entity?: string | null;
  hint?: HintContext;
}) {
  const groups = groupReasons(reasons, lineNumberOf(lineLabel));
  return (
    <ul className="flex flex-col gap-2" aria-label={label}>
      {groups.map((group, index) => {
        const first = group.reasons[0];
        if (!first) {
          return null;
        }
        return group.reasons.length > 1 ? (
          <GroupItem
            key={`${first.rule_id}-group-${index}`}
            group={group}
            lineLabel={lineLabel}
            entity={entity}
            hint={hint}
          />
        ) : (
          <ReasonItem
            key={`${first.rule_id}-${first.line_key ?? ""}-${index}`}
            reason={first}
            lineLabel={lineLabel}
            entity={entity}
            hint={hint}
          />
        );
      })}
    </ul>
  );
}

function OtherNotes({
  notes,
  lineLabel,
  entity,
}: {
  notes: RuleReason[];
  lineLabel?: (lineKey: string) => string | null;
  entity?: string | null;
}) {
  const [open, setOpen] = useState(false);
  if (notes.length === 0) {
    return null;
  }
  return (
    <Collapsible.Root
      open={open}
      onOpenChange={(next) => {
        logger.debug("[rules] other notes", { open: next });
        setOpen(next);
      }}
    >
      <Collapsible.Trigger className="text-accent-fg-subtle text-xs font-semibold hover:underline">
        {RULES_NOTES_LABEL} ({notes.length})
      </Collapsible.Trigger>
      <Collapsible.Panel>
        <div className="mt-2">
          <ReasonList
            reasons={notes}
            lineLabel={lineLabel}
            label={RULES_NOTES_LABEL}
            entity={entity}
          />
        </div>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}

/**
 * The rule codes and whether the rules changed after the draft was made: for IT and for
 * a question to the helpdesk, folded away so sales read the sentences only.
 */
function TechnicalDetails({ reasons, changed }: { reasons: RuleReason[]; changed: boolean }) {
  const [open, setOpen] = useState(false);
  if (reasons.length === 0 && !changed) {
    return null;
  }
  return (
    <Collapsible.Root open={open} onOpenChange={setOpen}>
      <Collapsible.Trigger className="text-fg-subtle text-xs font-semibold hover:underline">
        Technical details
      </Collapsible.Trigger>
      <Collapsible.Panel>
        <ul className="mt-2 flex flex-col gap-0.5">
          {reasons.map((reason, index) => (
            <li
              key={`${reason.rule_id}-${reason.line_key ?? ""}-${index}`}
              className="text-fg-subtle font-mono text-xs break-all"
            >
              {reason.rule_id}
              {reason.entity ? ` · ${entityLabel(reason.entity)}` : ""}
            </li>
          ))}
        </ul>
        {changed && (
          <Typography variant="caption" tone="muted" className="mt-1 block">
            {RULES_CHANGED_SENTENCE}
          </Typography>
        )}
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}

/**
 * The reasons of a case. Renders nothing without a reason; with notes only, just the
 * collapsed "Other notes". The rule codes are under "Technical details".
 */
export function RuleFlagsPanel({
  rules,
  entity,
  lineLabel,
  className,
  onApprovePrice,
}: {
  rules: CaseRules | null | undefined;
  /** The company of the case, for the link to its rules. */
  entity?: string | null;
  /** "Line 2" for a line key; null when the line is not shown. */
  lineLabel?: (lineKey: string) => string | null;
  className?: string;
  /**
   * Approves the held price of a line by its key. With it, a viewer who may approve prices
   * gets a working "Approve price" on the held reason; without it there is no button.
   */
  onApprovePrice?: (lineKey: string) => void;
}) {
  const { data: me } = useMe();
  const areas = useAreaLabels();
  const blocking = reasonsOf(rules, "blocking");
  const warnings = reasonsOf(rules, "warning");
  const notes = reasonsOf(rules, "info");
  if (!rules || blocking.length + warnings.length + notes.length === 0) {
    return null;
  }
  const attention = blocking.length + warnings.length > 0;
  const mayOpenRules = hasCapability(me, "see_rules");
  const rulesHref = entity ? `/rules/live?entity=${encodeURIComponent(entity)}` : "/rules/live";
  const hint: HintContext = {
    mayApprove: hasCapability(me, "approve_price"),
    area: entity ? areas.label(entity) : null,
    onApprovePrice,
  };

  return (
    <Card variant="outline" padding="none" className={className}>
      <Card.Header padding="sm" className="border-border-subtle flex items-center gap-2 border-b">
        <Typography variant="label">{attention ? RULES_PANEL_TITLE : RULES_NOTES_LABEL}</Typography>
        {blocking.length > 0 && (
          <Badge tone="danger" size="xs" className="tabular-nums">
            {blocking.length} {blocking.length === 1 ? "holds" : "hold"} the case
          </Badge>
        )}
        {warnings.length > 0 && (
          <Badge tone="warning" size="xs" className="tabular-nums">
            {warnings.length} to check
          </Badge>
        )}
        {mayOpenRules && (
          <Link
            to={rulesHref}
            className="text-accent-fg-subtle ml-auto text-xs font-semibold hover:underline"
          >
            Live settings
          </Link>
        )}
      </Card.Header>
      <Card.Body className="flex flex-col gap-3">
        {blocking.length > 0 && (
          <Alert tone="danger" title={RULES_HELD_TITLE}>
            <ReasonList
              reasons={blocking}
              lineLabel={lineLabel}
              label={RULES_HELD_TITLE}
              entity={entity}
              hint={hint}
            />
          </Alert>
        )}
        {warnings.length > 0 && (
          <ReasonList
            reasons={warnings}
            lineLabel={lineLabel}
            label="Reasons to check"
            entity={entity}
            hint={hint}
          />
        )}
        {attention && rules.customer_basis && (
          <Typography variant="caption" tone="subtle">
            {CUSTOMER_BASIS_TEXT[rules.customer_basis]}
          </Typography>
        )}
        <OtherNotes notes={notes} lineLabel={lineLabel} entity={entity} />
        {/* Rule codes are for IT and the rules owners, not for sales (P28). */}
        {(mayOpenRules || hasCapability(me, "triage_feedback")) && (
          <TechnicalDetails
            reasons={[...blocking, ...warnings, ...notes]}
            changed={Boolean(rules.changed_since_draft)}
          />
        )}
      </Card.Body>
    </Card>
  );
}

/**
 * The blocking and warning reasons of one line, compact, for the lines table. With
 * `onApprovePrice` (a viewer who may approve prices, on an open case) a held price gets a
 * working "Approve price"; without it there is no such button.
 */
export function LineRuleFlags({
  reasons,
  onApprovePrice,
  busy = false,
}: {
  reasons: RuleReason[];
  onApprovePrice?: () => void;
  /** True while a write of the case runs. */
  busy?: boolean;
}) {
  if (reasons.length === 0) {
    return null;
  }
  return (
    <ul className="flex min-w-0 flex-col gap-1" aria-label="Rule reasons on this line">
      {reasons.map((reason, index) => (
        <li
          key={`${reason.rule_id}-${index}`}
          className="flex min-w-0 flex-col gap-0.5"
          data-rule={reason.rule_id}
          data-severity={reason.severity}
          title={reason.action ? `${reason.message} ${reason.action}` : reason.message}
        >
          <span className="flex flex-wrap items-center gap-1">
            <Badge tone={SEVERITY_META[reason.severity].tone} size="xs" className="w-fit">
              {SEVERITY_META[reason.severity].label}
            </Badge>
            <WhyLink
              ruleId={reason.rule_id}
              entity={reason.entity}
              severity={reason.severity}
              className="h-auto px-1 py-0.5 text-xs"
            />
          </span>
          <span className="text-fg-muted line-clamp-2 text-xs break-words whitespace-normal">
            {withSignalWords(reason.message)}
          </span>
          {reason.rule_id === PRICE_APPROVAL_RULE &&
            reason.severity === "blocking" &&
            onApprovePrice && (
              // The approval itself: one click, the hold of this line is gone.
              <Button
                size="sm"
                variant="outline"
                tone="accent"
                disabled={busy}
                className="w-fit"
                startIcon={<Icon icon={Check} size="xs" />}
                onClick={onApprovePrice}
                data-approve-price
              >
                Approve price
              </Button>
            )}
        </li>
      ))}
    </ul>
  );
}
